import React, { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown } from 'lucide-react'
import { MetaValue } from './MetaValue'

export interface EntryCardProps {
  title: string
  /** Right-aligned value shown in the collapsed header (e.g. an ownership %). */
  meta?: React.ReactNode
  children: React.ReactNode
  /** Start expanded. Defaults to collapsed. */
  defaultOpen?: boolean
}

/**
 * Collapsible sub-card for one person/company inside a section. The header row
 * is the toggle: `title` on the left, `meta` flush right, chevron rotates on
 * open, and the field grid expands below. Shared by the read-only application
 * detail and review screens so both read identically.
 */
export function EntryCard({ title, meta, children, defaultOpen = false }: EntryCardProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div
      style={{
        background: 'var(--color-surface-base)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-3)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          width: '100%',
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-md)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
          }}
        >
          {title}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
          }}
        >
          {meta != null && <MetaValue>{meta}</MetaValue>}
          <ChevronDown
            size={18}
            aria-hidden="true"
            style={{
              flexShrink: 0,
              color: 'var(--color-text-secondary)',
              transition: 'transform var(--duration-standard) var(--ease-out)',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                gap: 'var(--spacing-3)',
              }}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
